import Experience from "@folio-2026/experience"
import { hexToRgb, randomHash } from "@folio-2026/shared"
import {
  atan,
  cos,
  float,
  Fn,
  If,
  instancedArray,
  instanceIndex,
  positionLocal,
  sin,
  smoothstep,
  uniform,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

export default class Particles {
  experience: Experience
  renderer: Experience["renderer"]["instance"]

  scene: THREE.Scene
  debugFolder?: FolderApi

  positions: THREE.StorageBufferNode<"vec3">
  weights: THREE.StorageBufferNode<"float">
  velocities: THREE.StorageBufferNode<"vec3">
  rotations: THREE.StorageBufferNode<"vec3">
  colors: THREE.StorageBufferNode<"vec3">
  horizontalRadii: THREE.StorageBufferNode<"float">
  computeParticles!: THREE.ComputeNode
  material!: THREE.MeshBasicNodeMaterial

  settings = {
    maxParticleCount: 100,
    gravity: uniform(0.02),
    animationType: uniform(0),
    radius: 6,
    rotationSpeed: uniform(-0.5),
    particleRotationSpeed: uniform(0.4),
    emissiveIntensity: uniform(0.17),
    size: uniform(0.4),
    freeFromParticlesRadius: uniform(3.5),
  }

  time = uniform(0)
  deltaTime = uniform(0)
  clock = new THREE.Clock()

  constructor({
    scene,
    debugFolder,
  }: {
    scene: THREE.Scene
    debugFolder?: FolderApi
  }) {
    this.experience = Experience.getInstance()
    this.renderer = this.experience.renderer.instance
    this.scene = scene
    this.debugFolder = debugFolder

    this.positions = instancedArray(this.settings.maxParticleCount, "vec3")
    this.weights = instancedArray(this.settings.maxParticleCount, "float")
    this.velocities = instancedArray(this.settings.maxParticleCount, "vec3")
    this.rotations = instancedArray(this.settings.maxParticleCount, "vec3")
    this.colors = instancedArray(this.settings.maxParticleCount, "vec3")
    this.horizontalRadii = instancedArray(
      this.settings.maxParticleCount,
      "float",
    )

    this.createMeshes()
    this.setDebug()
    this.init()
  }

  createMeshes() {
    const geometry = new THREE.BoxGeometry(0.2, 0.2, 0.2, 1, 1, 1)
    const edges = new THREE.EdgesGeometry(geometry)
    this.material = new THREE.MeshBasicNodeMaterial()

    // Apply per-particle color from storage buffer
    this.material.colorNode = this.colors.toAttribute()

    // Apply the same per-particle color to emissive using emissiveNode
    // The emissive color will match each particle's base color
    ;(this.material as unknown as THREE.MeshPhysicalNodeMaterial).emissiveNode =
      this.colors.toAttribute().mul(this.settings.emissiveIntensity)

    // material.opacityNode = shapeCircle();

    // Apply rotation and position to each instance
    this.material.positionNode = Fn(() => {
      const localPos = positionLocal
      const instancePos = this.positions.toAttribute()
      const rotation = this.rotations.toAttribute()

      // Helper function to apply Euler rotation using precomputed rotation matrix
      // Optimized: combines Z * Y * X rotations into a single 3x3 matrix multiplication
      const applyEulerRotation = (
        pos: typeof localPos,
        rot: ReturnType<THREE.StorageBufferNode<"vec3">["toAttribute"]>,
      ) => {
        // Precompute sin and cos for each axis (used multiple times)
        const cosX = cos(rot.x)
        const sinX = sin(rot.x)
        const cosY = cos(rot.y)
        const sinY = sin(rot.y)
        const cosZ = cos(rot.z)
        const sinZ = sin(rot.z)

        // Precompute combined rotation matrix elements (Z * Y * X order)
        // This avoids intermediate calculations from sequential rotations
        const m00 = cosY.mul(cosZ)
        const m01 = sinX.mul(sinY).mul(cosZ).sub(cosX.mul(sinZ))
        const m02 = cosX.mul(sinY).mul(cosZ).add(sinX.mul(sinZ))

        const m10 = cosY.mul(sinZ)
        const m11 = sinX.mul(sinY).mul(sinZ).add(cosX.mul(cosZ))
        const m12 = cosX.mul(sinY).mul(sinZ).sub(sinX.mul(cosZ))

        const m20 = sinY.negate()
        const m21 = sinX.mul(cosY)
        const m22 = cosX.mul(cosY)

        // Apply combined rotation matrix in single matrix-vector multiplication
        const x = pos.x
        const y = pos.y
        const z = pos.z

        return vec3(
          m00.mul(x).add(m01.mul(y)).add(m02.mul(z)),
          m10.mul(x).add(m11.mul(y)).add(m12.mul(z)),
          m20.mul(x).add(m21.mul(y)).add(m22.mul(z)),
        )
      }

      // Calculate scale based on distance from center
      // Use cached horizontal radius from storage buffer
      const horizontalRadius = this.horizontalRadii.toAttribute()

      // Normalize radius: 0 (center) to 1 (max radius of 12)
      const normalizedRadius = horizontalRadius.div(this.settings.radius)

      // Scale from 0.1 (at center) to 1.0 (at edge)
      // Using smoothstep for smooth scaling transition
      const scale = smoothstep(0.0, 1.0, normalizedRadius).mul(0.9).add(0.1)

      // Apply initial size to local position before rotation
      const sizedPos = localPos.mul(this.settings.size)

      // Apply Euler rotation to local position (toAttribute() returns compatible vec3 node; TSL types expect AttributeNode)
      // @ts-expect-error - Node<"vec3"> from toAttribute() is compatible but typed as AttributeNode<"vec3">
      const rotatedPos = applyEulerRotation(sizedPos, rotation)

      // Scale the rotated position
      const scaledRotatedPos = rotatedPos.mul(scale)

      // Add instance position offset
      return scaledRotatedPos.add(instancePos)
    })()

    const particles = new THREE.LineSegments(edges, this.material)
    particles.frustumCulled = false
    particles.count = this.settings.maxParticleCount

    this.scene.add(particles)
  }

  init() {
    const computeInit = Fn(() => {
      const position = this.positions.element(instanceIndex)
      const rotation = this.rotations.element(instanceIndex)
      const color = this.colors.element(instanceIndex)

      // Helper function to generate random hash value with seed
      const getRandomHash = (seedOffset: number) => {
        return randomHash(float(seedOffset))
      }

      // Define 5 colors as hex values
      const color1 = hexToRgb(0xebf7ff) // Green
      const color2 = hexToRgb(0x82aba1) // Blue
      const color3 = hexToRgb(0x225560) // Orange
      const color4 = hexToRgb(0xa9afd1) // Pink
      const color5 = hexToRgb(0x7c809b) // Purple

      // Select random color index (0-4)
      const colorIndex = getRandomHash(6).mul(5.0).floor()

      // Assign color based on index using conditional assignments
      If(colorIndex.equal(0), () => {
        color.x = color1.x
        color.y = color1.y
        color.z = color1.z
      })
        .ElseIf(colorIndex.equal(1), () => {
          color.x = color2.x
          color.y = color2.y
          color.z = color2.z
        })
        .ElseIf(colorIndex.equal(2), () => {
          color.x = color3.x
          color.y = color3.y
          color.z = color3.z
        })
        .ElseIf(colorIndex.equal(3), () => {
          color.x = color4.x
          color.y = color4.y
          color.z = color4.z
        })
        .Else(() => {
          color.x = color5.x
          color.y = color5.y
          color.z = color5.z
        })

      // Initialize random position in circular distribution
      // Random angle for circular distribution (0 to 2π)
      const angle = getRandomHash(0).mul(2.0).mul(3.14159)

      // Random radius (0 to 1), heavily biased towards edges
      // Using pow(4) creates a strong bias - most particles will be near the edges
      const randomRadius = getRandomHash(1)

      // Calculate available radius range (excluding freeFromParticlesRadius from center)
      const freeRadius = this.settings.freeFromParticlesRadius
      const maxRadius = uniform(this.settings.radius)
      const availableRadius = maxRadius.sub(freeRadius)

      // Map random radius to range [freeFromParticlesRadius, radius]
      const radius = freeRadius.add(
        uniform(1.0).sub(randomRadius.pow(4)).mul(availableRadius),
      )

      // Convert polar coordinates to cartesian (circular area)
      position.x = cos(angle).mul(radius)
      position.z = sin(angle).mul(radius)

      // Random Y position
      const y = getRandomHash(2)
      position.y = y.mul(10)

      // Initialize random rotation for each particle (0 to 2π for each axis)
      rotation.x = getRandomHash(3).mul(2.0).mul(3.14159)
      rotation.y = getRandomHash(4).mul(2.0).mul(3.14159)
      rotation.z = getRandomHash(5).mul(2.0).mul(3.14159)

      // Initialize horizontal radius cache
      const horizontalRadius = this.horizontalRadii.element(instanceIndex)
      horizontalRadius.assign(vec3(position.x, 0.0, position.z).length())
    })().compute(this.settings.maxParticleCount)

    const computeUpdate = Fn(() => {
      const position = this.positions.element(instanceIndex)
      const horizontalRadius = this.horizontalRadii.element(instanceIndex)
      const maxRadius = uniform(this.settings.radius)

      // Apply animation based on type (inline logic so storage writes are in same compute body as GridParticles)
      If(this.settings.animationType.equal(0), () => {
        // Rise: move Y up with speed multiplier by radius
        const radius = vec3(position.x, 0.0, position.z).length()
        horizontalRadius.assign(radius)
        const speedMultiplier = maxRadius.div(radius.add(0.1))
        const riseDelta = this.settings.gravity.mul(speedMultiplier)
        position.y = position.y.add(riseDelta)
        If(position.y.greaterThan(10), () => {
          position.y = position.y.mul(0.0)
        })
      }).Else(() => {
        // Vortex: rotate around Y axis
        const radius = vec3(position.x, 0.0, position.z).length()
        horizontalRadius.assign(radius)
        const currentAngle = atan(position.z, position.x)
        const speedMultiplier = maxRadius.div(radius.add(0.1))
        const newAngle = currentAngle.add(
          this.settings.rotationSpeed.div(10).mul(speedMultiplier),
        )
        position.x = cos(newAngle).mul(radius)
        position.z = sin(newAngle).mul(radius)
        horizontalRadius.assign(radius)
      })

      // Update rotation for all particles (continues during all animations)
      const deltaRotation = this.deltaTime.mul(
        this.settings.particleRotationSpeed,
      )

      this.rotations
        .element(instanceIndex)
        .addAssign(vec3(deltaRotation, deltaRotation, deltaRotation))
    })

    this.computeParticles = computeUpdate().compute(
      this.settings.maxParticleCount,
    )

    this.renderer.computeAsync(computeInit)
  }

  update() {
    // Update time and deltaTime uniforms for compute shader
    const dt = this.clock.getDelta()
    this.time.value += dt
    this.deltaTime.value = dt

    this.renderer.compute(this.computeParticles)
  }

  setDebug() {
    if (this.debugFolder) {
      const config = {
        gravity: this.settings.gravity.value,
        animationType: this.settings.animationType.value,
        rotationSpeed: this.settings.rotationSpeed.value,
        particleRotationSpeed: this.settings.particleRotationSpeed.value,
        size: this.settings.size.value,
        freeFromParticlesRadius: this.settings.freeFromParticlesRadius.value,
      }

      const folder = this.debugFolder.addFolder({
        title: "🌟 Particles",
        expanded: false,
      })

      folder
        .addBinding(config, "animationType", {
          min: 0,
          max: 1,
          step: 1,
        })
        .on("change", (ev) => {
          this.settings.animationType.value = ev.value
        })

      folder
        .addBinding(config, "gravity", {
          min: 0,
          max: 0.1,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.settings.gravity.value = ev.value
        })

      folder
        .addBinding(config, "rotationSpeed", {
          min: -1,
          max: 1,
          step: 0.001,
        })
        .on("change", (ev) => {
          this.settings.rotationSpeed.value = ev.value
        })

      folder
        .addBinding(config, "particleRotationSpeed", {
          min: 0,
          max: 5,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.settings.particleRotationSpeed.value = ev.value
        })

      folder
        .addBinding(config, "size", {
          min: 0.01,
          max: 2,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.settings.size.value = ev.value
        })

      folder
        .addBinding(config, "freeFromParticlesRadius", {
          min: 0,
          max: 7,
          step: 0.1,
        })
        .on("change", (ev) => {
          this.settings.freeFromParticlesRadius.value = ev.value
        })

      // Emissive controls
      if (this.material) {
        const emissiveSettings = {
          emissiveIntensity: this.settings.emissiveIntensity.value,
        }

        folder
          .addBinding(emissiveSettings, "emissiveIntensity", {
            min: 0,
            max: 2,
            step: 0.01,
          })
          .on("change", (ev) => {
            this.settings.emissiveIntensity.value = ev.value
          })
      }
    }
  }
}
